import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Clock3, KeyRound, ListChecks, Loader2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-[#2E2A22]/15 bg-white px-4 py-3 text-base uppercase tracking-wide text-[#2E2A22] outline-none transition-colors focus:border-[#A66E08] focus:ring-2 focus:ring-[#A66E08]/20";

const MESSAGES = {
  autre_etablissement: "Ce code ne correspond pas à votre établissement.",
  expire: "Ce code a expiré.",
  deja_utilise: "Ce code a déjà été utilisé.",
  inconnu: "Code inconnu.",
};

const ResultCard = ({ result, onConfirm, confirming }) => {
  if (!result) return null;
  if (result.status === "valide") {
    return (
      <div data-testid="partenaire-result-valide" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <p className="flex items-center gap-2 font-semibold text-emerald-700">
          <CheckCircle2 className="h-5 w-5" /> Code valide
        </p>
        <div className="mt-3 space-y-1 text-sm text-[#2E2A22]">
          <p><span className="font-semibold">Bénéficiaire :</span> <span data-testid="partenaire-beneficiaire">{result.beneficiaire}</span></p>
          {result.experience && <p><span className="font-semibold">Expérience :</span> {result.experience}</p>}
          {result.valide_jusqu_au && <p><span className="font-semibold">Valable jusqu'au :</span> {result.valide_jusqu_au}</p>}
        </div>
        <button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="partenaire-confirmer-btn"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
        >
          {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
          Confirmer la venue
        </button>
      </div>
    );
  }
  if (result.status === "confirme") {
    return (
      <div data-testid="partenaire-result-confirme" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" strokeWidth={1.5} />
        <p className="mt-2 font-semibold text-emerald-700">Venue confirmée. Merci !</p>
        <p className="mt-1 text-sm text-[#2E2A22]">
          {result.beneficiaire}{result.experience ? ` — ${result.experience}` : ""}
        </p>
      </div>
    );
  }
  const icon = result.status === "expire" ? <Clock3 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />;
  return (
    <div data-testid={`partenaire-result-${result.status}`} className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 p-5 font-semibold text-red-600">
      {icon} {MESSAGES[result.status] || "Code inconnu."}
    </div>
  );
};

export default function PartenaireVerif() {
  const [params] = useSearchParams();
  const [codePart, setCodePart] = useState((params.get("code") || "").toUpperCase());
  const [codeBenef, setCodeBenef] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [liste, setListe] = useState(null);
  const [listeLoading, setListeLoading] = useState(false);

  const payload = { code_partenaire: codePart.trim().toUpperCase(), code: codeBenef.trim().toUpperCase() };

  const verifier = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/partenaire/verifier", payload);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Vérification impossible");
    } finally {
      setLoading(false);
    }
  };

  const confirmer = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/partenaire/confirmer", payload);
      setResult(data);
      if (data.status === "confirme") {
        toast.success("Venue confirmée");
        setListe(null);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Confirmation impossible");
    } finally {
      setConfirming(false);
    }
  };

  const voirListe = async () => {
    if (!codePart.trim()) {
      toast.error("Saisissez d'abord votre code partenaire.");
      return;
    }
    setListeLoading(true);
    try {
      const { data } = await api.post("/partenaire/codes", { code_partenaire: codePart.trim().toUpperCase(), code: "" });
      setListe(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Liste indisponible");
    } finally {
      setListeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7EF] text-[#2E2A22]">
      <header className="border-b border-[#2E2A22]/8 bg-white/70">
        <div className="mx-auto flex max-w-xl items-center justify-between px-5 py-4">
          <Link to="/" data-testid="partenaire-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#A66E08]">
            <ArrowLeft className="h-4 w-4" /> Site T'POZ
          </Link>
          <img src="/logo-tpoz.png" alt="T'POZ" className="h-9" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
      </header>

      <main className="mx-auto max-w-xl px-5 py-8">
        <h1 className="font-serif text-3xl leading-tight">Espace partenaire</h1>
        <p className="mt-2 text-sm text-[#6B6355]">
          Vérifiez le code T'POZ d'une bénéficiaire et confirmez sa venue. Aucun compte nécessaire.
        </p>

        <form onSubmit={verifier} className="mt-6 space-y-4 rounded-3xl border border-[#2E2A22]/8 bg-white p-5 sm:p-6">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#6B6355]">
              <KeyRound className="h-3.5 w-3.5 text-[#A66E08]" /> Votre code partenaire
            </label>
            <input
              data-testid="partenaire-code-input"
              value={codePart}
              onChange={(e) => { setCodePart(e.target.value); setResult(null); }}
              placeholder="PART-7K3M9Q"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#6B6355]">
              Code de la bénéficiaire
            </label>
            <input
              data-testid="benef-code-input"
              value={codeBenef}
              onChange={(e) => { setCodeBenef(e.target.value); setResult(null); }}
              placeholder="TPZ-2026-X8QF"
              required
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="partenaire-verifier-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2E2A22] px-6 py-3.5 text-sm font-semibold text-[#FBF7EF] transition-colors hover:bg-[#A66E08] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Vérifier le code
          </button>
          <ResultCard result={result} onConfirm={confirmer} confirming={confirming} />
        </form>

        <div className="mt-6 rounded-3xl border border-[#2E2A22]/8 bg-white p-5 sm:p-6">
          <button
            onClick={voirListe}
            disabled={listeLoading}
            data-testid="partenaire-liste-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#A66E08]/50 px-6 py-3 text-sm font-semibold text-[#A66E08] transition-colors hover:bg-[#A66E08]/10 disabled:opacity-60"
          >
            {listeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
            Voir mes codes en attente et mes venues
          </button>

          {liste && (
            <div className="mt-5 space-y-5" data-testid="partenaire-liste">
              <p className="text-sm font-semibold text-[#2E2A22]">{liste.partenaire}</p>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B6355]">Codes en attente chez vous ({liste.en_attente.length})</p>
                <div className="mt-2 space-y-2">
                  {liste.en_attente.length === 0 && <p className="text-sm text-[#6B6355]">Aucun code en attente.</p>}
                  {liste.en_attente.map((c) => (
                    <div key={c.code} data-testid={`liste-attente-${c.code}`} className="rounded-xl bg-[#FBF7EF] px-4 py-3 text-sm">
                      <p className="font-bold text-[#A66E08]">{c.code}</p>
                      <p>{c.beneficiaire}{c.experience ? ` — ${c.experience}` : ""}</p>
                      {c.valide_jusqu_au && <p className="text-xs text-[#6B6355]">Valable jusqu'au {c.valide_jusqu_au}</p>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B6355]">Venues confirmées ({liste.confirmees.length})</p>
                <div className="mt-2 space-y-2">
                  {liste.confirmees.length === 0 && <p className="text-sm text-[#6B6355]">Aucune venue confirmée pour le moment.</p>}
                  {liste.confirmees.map((c) => (
                    <div key={c.code} data-testid={`liste-venue-${c.code}`} className="rounded-xl bg-emerald-50 px-4 py-3 text-sm">
                      <p className="font-bold text-emerald-700">{c.code}</p>
                      <p>{c.beneficiaire}{c.experience ? ` — ${c.experience}` : ""}</p>
                      {c.venue_le && <p className="text-xs text-[#6B6355]">Venue le {c.venue_le}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-[#6B6355]">
          Une question ? Association Plus Belle La Vie 971 · 0690 74 59 10
        </p>
      </main>
    </div>
  );
}
